import type { ComponentProps, ReactNode, Ref } from "react";

import { cn } from "./cn";
import { Icon, type IconName } from "./icon";

// A short message about the state of the page: a warning, a hint for staff, a confirmation, a refusal. It always has a glyph AND words, never
// colour alone. It takes any content (a button or several paragraphs may sit inside). Say what is urgent with `role="alert"`; a polite update
// has `role="status"`; a plain remark has `role="note"` or none.

type NoticeTone = "neutral" | "info" | "success" | "warning" | "danger";

const tones = {
  neutral: { box: "border-border bg-muted text-foreground", icon: "information-circle" },
  info: { box: "border-info-border bg-info-soft text-info", icon: "information-circle" },
  success: {
    box: "border-success-border bg-success-soft text-success",
    icon: "checkmark-circle-02",
  },
  warning: { box: "border-warning-border bg-warning-soft text-warning", icon: "alert-02" },
  danger: {
    box: "border-destructive-border bg-destructive-soft text-destructive",
    icon: "cancel-circle",
  },
} as const satisfies Record<NoticeTone, { box: string; icon: IconName }>;

type NoticeProps = Omit<ComponentProps<"div">, "ref" | "children"> &
  Readonly<{
    tone?: NoticeTone;
    children: ReactNode;
    /** Makes the notice focusable by code (`tabIndex=-1`), so a page can move focus to a result it has just announced. */
    focusRef?: Ref<HTMLDivElement>;
    /** A full-width bar under a pane header (square corners, no side or top border) instead of a box. */
    flush?: boolean;
    /** Replaces the glyph of the tone (the offline icon). It is decoration (`aria-hidden`): the words say what it says. */
    icon?: ReactNode;
  }>;

export function Notice({
  tone = "neutral",
  className,
  children,
  focusRef,
  flush = false,
  icon,
  ...props
}: NoticeProps) {
  const { box, icon: glyph } = tones[tone];
  return (
    <div
      ref={focusRef}
      tabIndex={focusRef ? -1 : undefined}
      className={cn(
        "flex items-start gap-2 border text-sm leading-6",
        flush ? "rounded-none border-x-0 border-t-0 px-4 py-2.5" : "rounded-sm px-3.5 py-2.5",
        box,
        className,
      )}
      {...props}
    >
      {icon === undefined ? (
        <Icon name={glyph} className="mt-1" />
      ) : (
        <span aria-hidden="true" className="mt-1 inline-flex shrink-0">
          {icon}
        </span>
      )}
      <div className="grid min-w-0 flex-1 justify-items-start gap-2 [overflow-wrap:anywhere] [&_p]:m-0">
        {children}
      </div>
    </div>
  );
}
